import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, 
  Search,
  ChevronLeft,
  Trash2,
  Ban,
  CheckCircle,
  Wallet,
  Loader2,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { formatDateTime, getEloRank, formatCurrency } from '../lib/utils';

export default function AdminPlayers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  
  // Balance Dialog State
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    fetchPlayers();
  }, [user, navigate]);

  const fetchPlayers = async () => {
    try {
      const response = await api.get('/admin/users?role=player');
      setPlayers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (playerId, playerName) => {
    if (!window.confirm(`Spieler "${playerName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;

    setActionLoading(playerId);
    try {
      await api.delete(`/admin/users/${playerId}`);
      toast.success('Spieler gelöscht');
      await fetchPlayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBlock = async (playerId, playerName) => {
    if (!window.confirm(`Spieler "${playerName}" wirklich blockieren? Der Spieler kann sich nicht mehr einloggen.`)) return;

    setActionLoading(playerId);
    try {
      await api.put(`/admin/users/${playerId}/block`);
      toast.success('Spieler blockiert');
      await fetchPlayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Blockieren');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnblock = async (playerId, playerName) => {
    setActionLoading(playerId);
    try {
      await api.put(`/admin/users/${playerId}/unblock`);
      toast.success(`Spieler "${playerName}" wurde entsperrt`);
      await fetchPlayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Entsperren');
    } finally {
      setActionLoading(null);
    }
  };

  const openBalanceDialog = (player) => {
    setSelectedPlayer(player);
    setBalanceAmount('');
    setBalanceDialogOpen(true);
  };

  const handleAddBalance = async () => {
    const amount = parseFloat(balanceAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Bitte gib einen gültigen Betrag ein');
      return;
    }

    setActionLoading(selectedPlayer.id);
    try {
      const response = await api.post(`/admin/users/${selectedPlayer.id}/add-balance`, { amount });
      toast.success(`${amount.toFixed(2)} TND wurden dem Konto von ${selectedPlayer.name} gutgeschrieben. Neuer Kontostand: ${response.data.new_balance.toFixed(2)} TND`);
      setBalanceDialogOpen(false);
      await fetchPlayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aufladen');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredPlayers = players.filter(p =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
          <Skeleton className="h-6 sm:h-8 w-48 sm:w-64 mb-4 sm:mb-8" />
          <Skeleton className="h-80 sm:h-96 rounded-xl sm:rounded-2xl" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-24" data-testid="admin-players">
        {/* Header */}
        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} className="flex-shrink-0">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-bold text-xl sm:text-2xl text-tunis-blue">
              Spieler verwalten
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">{players.length} registrierte Spieler</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4 sm:mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
          <Input
            placeholder="Spieler suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 sm:pl-10 h-10 sm:h-12 text-sm sm:text-base"
          />
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <Card className="border-slate-200">
            <CardContent className="p-3 sm:p-4 text-center">
              <div className="text-lg sm:text-2xl font-bold text-tunis-blue">{players.length}</div>
              <div className="text-[10px] sm:text-xs text-muted-foreground">Gesamt</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-3 sm:p-4 text-center">
              <div className="text-lg sm:text-2xl font-bold text-green-600">
                {players.filter(p => !p.is_blocked && p.is_active !== false).length}
              </div>
              <div className="text-[10px] sm:text-xs text-muted-foreground">Aktiv</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-3 sm:p-4 text-center">
              <div className="text-lg sm:text-2xl font-bold text-red-600">
                {players.filter(p => p.is_blocked).length}
              </div>
              <div className="text-[10px] sm:text-xs text-muted-foreground">Blockiert</div>
            </CardContent>
          </Card>
        </div>

        {/* Mobile Card View */}
        <div className="sm:hidden space-y-3">
          {filteredPlayers.map((player) => {
            const eloRank = getEloRank(player.elo_points);
            const isBlocked = player.is_blocked;
            const isDeleted = player.is_active === false;
            
            return (
              <Card key={player.id} className={`border-slate-200 ${isBlocked ? 'bg-red-50 border-red-200' : ''} ${isDeleted ? 'bg-gray-100 opacity-60' : ''}`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">{player.name}</span>
                        {isBlocked && (
                          <Badge className="bg-red-100 text-red-700 text-[10px]">
                            <Ban className="w-2.5 h-2.5 mr-0.5" />
                            Blockiert
                          </Badge>
                        )}
                        {isDeleted && (
                          <Badge className="bg-gray-200 text-gray-600 text-[10px]">Gelöscht</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate mt-0.5">
                        {player.email || player.phone || '-'}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={actionLoading === player.id}>
                          {actionLoading === player.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <MoreVertical className="w-4 h-4" />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openBalanceDialog(player)} className="text-tunis-teal">
                          <Wallet className="w-4 h-4 mr-2" />
                          Guthaben aufladen
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {isBlocked ? (
                          <DropdownMenuItem onClick={() => handleUnblock(player.id, player.name)} className="text-green-600">
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Entsperren
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => handleBlock(player.id, player.name)} className="text-amber-600">
                            <Ban className="w-4 h-4 mr-2" />
                            Blockieren
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleDelete(player.id, player.name)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs">
                    <Badge className={`${eloRank.bg} ${eloRank.color} text-[10px]`}>
                      {player.elo_points} ELO
                    </Badge>
                    <span className="text-muted-foreground">{player.matches_played} Spiele</span>
                    <span className="text-green-600">{player.wins} Siege</span>
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs">
                    <span className="text-muted-foreground">
                      Guthaben: <span className="font-medium text-tunis-teal">{formatCurrency(player.wallet_balance || 0)}</span>
                    </span>
                    <span className="text-muted-foreground">{formatDateTime(player.created_at)}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Desktop Table View */}
        <Card className="border-slate-200 hidden sm:block">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail / Telefon</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">ELO</TableHead>
                  <TableHead className="text-center">Spiele</TableHead>
                  <TableHead className="text-center">Siege</TableHead>
                  <TableHead className="text-right">Guthaben</TableHead>
                  <TableHead>Registriert</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPlayers.map((player) => {
                  const eloRank = getEloRank(player.elo_points);
                  const isBlocked = player.is_blocked;
                  const isDeleted = player.is_active === false;
                  
                  return (
                    <TableRow key={player.id} className={`${isBlocked ? 'bg-red-50' : ''} ${isDeleted ? 'bg-gray-100 opacity-60' : ''}`}>
                      <TableCell className="font-medium">{player.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {player.email || player.phone || '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        {isDeleted ? (
                          <Badge className="bg-gray-200 text-gray-600">Gelöscht</Badge>
                        ) : isBlocked ? (
                          <Badge className="bg-red-100 text-red-700">
                            <Ban className="w-3 h-3 mr-1" />
                            Blockiert
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Aktiv
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${eloRank.bg} ${eloRank.color}`}>
                          {player.elo_points}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">{player.matches_played}</TableCell>
                      <TableCell className="text-center text-green-600">{player.wins}</TableCell>
                      <TableCell className="text-right font-medium text-tunis-teal">
                        {formatCurrency(player.wallet_balance || 0)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDateTime(player.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-tunis-teal hover:bg-tunis-teal/10"
                            onClick={() => openBalanceDialog(player)}
                            disabled={actionLoading === player.id}
                            title="Guthaben aufladen"
                          >
                            <Wallet className="w-4 h-4" />
                          </Button>
                          {isBlocked ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-green-600 hover:bg-green-50"
                              onClick={() => handleUnblock(player.id, player.name)}
                              disabled={actionLoading === player.id}
                              title="Entsperren"
                            >
                              {actionLoading === player.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4" />
                              )}
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-amber-600 hover:bg-amber-50"
                              onClick={() => handleBlock(player.id, player.name)}
                              disabled={actionLoading === player.id}
                              title="Blockieren"
                            >
                              {actionLoading === player.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Ban className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(player.id, player.name)}
                            disabled={actionLoading === player.id}
                            title="Löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {filteredPlayers.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-muted-foreground">Keine Spieler gefunden</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Balance Dialog */}
        <Dialog open={balanceDialogOpen} onOpenChange={setBalanceDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-tunis-teal" />
                Guthaben aufladen
              </DialogTitle>
              <DialogDescription>
                Guthaben für <span className="font-medium text-tunis-blue">{selectedPlayer?.name}</span> aufladen
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-muted-foreground">Aktuelles Guthaben:</span>
                <span className="font-bold text-tunis-teal">
                  {formatCurrency(selectedPlayer?.wallet_balance || 0)}
                </span>
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Betrag (TND)</Label>
                <Input
                  id="amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="z.B. 50.00"
                  value={balanceAmount}
                  onChange={(e) => setBalanceAmount(e.target.value)}
                  className="h-12 text-lg"
                />
              </div>
              {balanceAmount && parseFloat(balanceAmount) > 0 && (
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg text-sm">
                  <span className="text-muted-foreground">Neues Guthaben:</span>
                  <span className="font-bold text-green-600">
                    {formatCurrency((selectedPlayer?.wallet_balance || 0) + parseFloat(balanceAmount))}
                  </span>
                </div>
              )}
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setBalanceDialogOpen(false)} className="w-full sm:w-auto">
                Abbrechen
              </Button>
              <Button 
                onClick={handleAddBalance} 
                disabled={!balanceAmount || parseFloat(balanceAmount) <= 0 || actionLoading}
                className="w-full sm:w-auto bg-tunis-teal hover:bg-tunis-teal/90"
              >
                {actionLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Wird aufgeladen...
                  </>
                ) : (
                  <>
                    <Wallet className="w-4 h-4 mr-2" />
                    Aufladen
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
